import { ArrowsClockwise, Pause, Play } from '@phosphor-icons/react';
import clsx from 'clsx';
import type { Scene } from '@/types';
import { useGenerationStore } from '@/state/generationStore';
import { useAutoPlay } from '@/hooks/useAutoPlay';

interface Props {
  scenes: Scene[];
  episodeUuid: string;
  onRefresh?: () => void;
}

export function GridToolbar({ scenes, episodeUuid, onRefresh }: Props) {
  const total = scenes.filter((s) => !s.deletedAt).length;
  const empty = scenes.filter((s) => !s.deletedAt && s.status === 'empty').length;
  const autoPlayState = useGenerationStore((s) => s.autoPlayState);
  const completedCount = useGenerationStore((s) => s.completedCount);
  const totalCount = useGenerationStore((s) => s.totalCount);
  const { start, stop } = useAutoPlay();

  const isRunning = autoPlayState === 'running';
  const isPaused = autoPlayState === 'paused';
  const canAutoPlay = empty > 0 && autoPlayState === 'idle';

  function onAutoPlay() {
    if (isRunning || isPaused) return stop();
    return start(episodeUuid);
  }

  return (
    <div className="sticky top-0 z-20 flex justify-end gap-3 pb-4">
      <div className="flex items-center gap-3 rounded-md bg-surface-1/80 px-3 py-2 backdrop-blur-md">
        <span className="text-[12px] tabular-nums text-text-3">
          {total} scenes ·{' '}
          <span className={empty > 0 ? 'text-accent' : 'text-text-4'}>{empty} empty</span>
        </span>

        <button
          type="button"
          onClick={onRefresh}
          aria-label="Refresh scenes"
          className="rounded-md p-1.5 text-text-3 transition-colors hover:bg-surface-2 hover:text-text-1"
        >
          <ArrowsClockwise size={16} />
        </button>

        <button
          type="button"
          onClick={onAutoPlay}
          disabled={!canAutoPlay && !isRunning && !isPaused}
          className={clsx(
            'relative inline-flex h-9 items-center gap-2 overflow-hidden rounded-md px-4 text-[13px] font-medium transition-all',
            isRunning && 'bg-surface-3 text-text-1',
            (canAutoPlay || isPaused) &&
              'bg-accent text-text-on-accent hover:bg-accent-hover hover:shadow-elev-focus',
            !canAutoPlay &&
              !isRunning &&
              !isPaused &&
              'cursor-not-allowed text-text-4',
          )}
          aria-label={
            isRunning
              ? 'Stop auto-play'
              : isPaused
                ? 'Resume auto-play'
                : 'Start auto-play'
          }
        >
          {isRunning ? (
            <>
              <Pause size={14} weight="fill" />
              Stop
              <span className="ml-1 font-mono text-[12px] tabular-nums opacity-70">
                {completedCount} / {totalCount}
              </span>
              <span
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-0.5 bg-accent transition-[width] duration-base"
                style={{
                  width: totalCount === 0 ? '0%' : `${(completedCount / totalCount) * 100}%`,
                }}
              />
            </>
          ) : (
            <>
              <Play size={14} weight="fill" />
              {isPaused ? 'Resume' : 'Auto-play'}
              {(canAutoPlay || isPaused) && (
                <span className="ml-0.5 font-mono text-[12px] tabular-nums opacity-70">
                  ({empty})
                </span>
              )}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
